import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * The bulk order grid on multi-variant product pages.
 *
 * Works like the live Neto "Buying Options" table: quantities are staged in the grid and
 * nothing reaches the cart until "Add selected to cart" is pressed, which adds every row
 * with a quantity in one request and opens the cart drawer. The total box shows the
 * selection, not the cart.
 *
 * (Horizon's quick-order-list, which this block used to run on, writes to the cart on every
 * +/- press and mirrors cart quantities back into the grid.)
 *
 * @typedef {object} BulkOrderGridRefs
 * @property {HTMLElement[]} variantRows - One row per variant, carrying data-variant-id and data-price
 * @property {HTMLElement} totalPrice - Selection total
 * @property {HTMLElement} itemsCount - Selection item count
 * @property {HTMLButtonElement} addButton - "Add selected to cart"
 * @property {HTMLElement} errorContainer - Error message wrapper
 * @property {HTMLElement} errorText - Error message text
 *
 * @extends Component<BulkOrderGridRefs>
 */
class BulkOrderGridComponent extends Component {
  requiredRefs = ['variantRows', 'totalPrice', 'itemsCount', 'addButton', 'errorContainer', 'errorText'];

  /** @type {number | undefined} */
  #errorTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#updateTotals);
    this.addEventListener('input', this.#updateTotals);
    this.#updateTotals();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#updateTotals);
    this.removeEventListener('input', this.#updateTotals);
    clearTimeout(this.#errorTimeout);
  }

  /**
   * Enter in a quantity field submits the form natively, which would navigate to /cart/add.
   * Treat it as pressing the button instead.
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    event.preventDefault();
    this.addSelected();
  }

  /** @returns {Array<{ row: HTMLElement, input: HTMLInputElement, quantity: number }>} */
  #selectedRows() {
    return this.refs.variantRows
      .map((row) => {
        const input = /** @type {HTMLInputElement | null} */ (row.querySelector('input[type="number"]'));
        const quantity = input ? parseInt(input.value, 10) || 0 : 0;
        return { row, input, quantity };
      })
      .filter((entry) => entry.input && entry.quantity > 0);
  }

  #updateTotals = () => {
    const selected = this.#selectedRows();
    const count = selected.reduce((sum, { quantity }) => sum + quantity, 0);
    const total = selected.reduce((sum, { row, quantity }) => sum + (Number(row.dataset.price) || 0) * quantity, 0);

    this.refs.itemsCount.textContent = String(count);
    this.refs.totalPrice.textContent = formatMoney(
      total,
      this.dataset.moneyFormat || '${{amount}}',
      this.dataset.currency || 'AUD'
    );
    this.refs.addButton.setAttribute('aria-disabled', String(count === 0));
    if (count > 0) this.#hideError();
  };

  /** @param {string} message */
  #showError(message) {
    const { errorContainer, errorText } = this.refs;
    errorText.textContent = message;
    errorContainer.classList.remove('hidden');
    clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => this.#hideError(), 6000);
  }

  #hideError() {
    this.refs.errorContainer.classList.add('hidden');
  }

  async addSelected() {
    const { addButton } = this.refs;
    if (addButton.getAttribute('aria-busy') === 'true') return;

    const selected = this.#selectedRows();
    if (selected.length === 0) {
      this.#showError(this.dataset.emptyMessage || 'Please choose a quantity first.');
      return;
    }

    const sectionIds = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    }

    const itemCount = selected.reduce((sum, { quantity }) => sum + quantity, 0);
    addButton.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: selected.map(({ row, quantity }) => ({ id: Number(row.dataset.variantId), quantity })),
          sections: sectionIds.join(','),
        }),
      });
      const data = await response.json();

      if (data.status) {
        this.#showError(data.description || data.message);
        this.dispatchEvent(new CartErrorEvent(this.id, data.message, data.description, data.errors));
        // Shopify can still add the in-stock rows of a partly failed request, so resync.
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'bulk-order-grid',
            itemCount,
            productId: this.dataset.productId,
          })
        );
        return;
      }

      for (const { input } of selected) {
        input.value = '0';
        input.closest('cart-quantity-selector-component')?.updateButtonStates?.();
      }
      this.#updateTotals();

      const cart = await fetch(`${Theme.routes.cart_url}.js`)
        .then((res) => res.json())
        .catch(() => undefined);

      this.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'bulk-order-grid',
          itemCount,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.#showError(this.dataset.errorMessage || 'Something went wrong. Please try again.');
    } finally {
      addButton.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('bulk-order-grid-component')) {
  customElements.define('bulk-order-grid-component', BulkOrderGridComponent);
}
